/**
 * Monta e recupera a URL de retorno do SSO.
 *
 * O guard de rota precisa guardar a rota inteira, com query string: os links que chegam
 * por e-mail (orçamento online, por exemplo) carregam o identificador em `?q=`, e sem ele
 * a tela de destino abre vazia depois do login.
 */

/**
 * Acrescenta `redirect=true` a uma URL que já pode ter query e/ou hash.
 *
 * Concatenar '?redirect=true' direto quebra quando a URL já tem query
 * (`?q=x?redirect=true` é inválido) e quando tem hash (o parâmetro acabaria dentro dele).
 */
export function comRedirect (url) {
    if (!url) return url

    const [semHash, ...hash] = String(url).split('#')
    const separador = semHash.includes('?') ? '&' : '?'
    const sufixo = hash.length ? '#' + hash.join('#') : ''

    return semHash + separador + 'redirect=true' + sufixo
}

/**
 * Monta a URL do login preservando a rota de retorno inteira.
 * O valor vai codificado para que a query da rota de destino não se misture com a do login.
 */
export function urlDeLoginCom (returnUrl) {
    return '/login?return_url=' + encodeURIComponent(returnUrl || '')
}

/**
 * Rota para voltar depois do SSO quando a sessão cai NO MEIO do uso (401 da API) e a URL não
 * traz `return_url`. O cookie `vuex` ainda vale, o guard deixa a tela abrir e só a API recusa —
 * sem isto, quem chega por um link direto (loja, e-mail) loga e cai na home (ClickUp 17ttyenaxrn).
 * Home, login (inclui expired/mfa) e logoff não são destino: devolve null. O `redirect` da URL
 * atual sai, porque o comRedirect põe o dele na volta.
 *
 * - `exigeLogin`: só tela que exige login é destino (fora: /unauthorized, /400, 404, públicas).
 * - `storage`: disjuntor — se ACABAMOS de devolver o usuário para esta mesma tela depois do SSO
 *   (registrarVolta) e ela deu 401 de novo, não guardar: senão 401 → SSO (que reloga sozinho) →
 *   mesma tela → 401 viraria um loop. O pior caso volta a ser "cai na home".
 */
export function rotaParaVoltar (local, { exigeLogin = true, storage = null, agora = Date.now() } = {}) {
    const caminho = (local && local.pathname) || ''
    if (!exigeLogin || !caminho || caminho === '/' || caminho === '/home' || caminho.startsWith('/login') || caminho.includes('logoff')) {
        return null
    }

    const query = new URLSearchParams((local && local.search) || '')
    query.delete('redirect')
    const texto = query.toString()
    const destino = caminho + (texto ? '?' + texto : '')
    return storage && voltouAgora(storage, destino, agora) ? null : destino
}

const CHAVE_VOLTA = 'sso_return_consumido'
const JANELA_DA_VOLTA_MS = 60 * 1000

/** Marca que o usuário acabou de ser devolvido a `url` depois do SSO (alimenta o disjuntor acima). */
export function registrarVolta (storage, url, agora = Date.now()) {
    if (!storage || !url) return
    const semRedirect = String(url).replace(/([?&])redirect=true(&|$)/, (_, antes, depois) => (depois ? antes : '')).replace(/[?&]$/, '')
    try {
        storage.setItem(CHAVE_VOLTA, JSON.stringify({ url: semRedirect, at: agora }))
    } catch (_) { /* storage indisponível: sem disjuntor, comportamento antigo */ }
}

function voltouAgora (storage, url, agora) {
    try {
        const volta = JSON.parse(storage.getItem(CHAVE_VOLTA) || 'null')
        return !!volta && volta.url === url && agora - volta.at < JANELA_DA_VOLTA_MS
    } catch (_) {
        return false
    }
}
